import axios from "axios";

import { languageStore } from "@/stores/preferences/language-store";

import { authStore } from "../../stores/auth/auth-store";
import { logout as logoutAuth, refresh as refreshAuth } from "../auth/auth-service";
import { AccountApi, CarrierApi, CompanyApi, Configuration, DeliveryApi, OnboardApi, ReportingApi, SecurityApi, SettingsApi, SubscriptionApi, UserApi } from "./lib";

/**
 * Fetches the API configuration from the `config.json` file.
 * @returns {Promise<{ apiUrl: string }>} The API configuration object.
 */
async function fetchApiConfig() {
  const response = await fetch("/config.json");
  if (!response.ok) {
    throw new Error("Failed to load API configuration");
  }
  return response.json();
}

/**
 * Axios instance for API communication. The base URL is dynamically set after fetching the configuration.
 */
const apiClient = axios.create({
  baseURL: "",
  paramsSerializer: (params) => {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      searchParams.append(key, value);
    }
    return searchParams.toString();
  },
});

apiClient.interceptors.request.use(async (config) => {
  const store = authStore.getState();

  // Don't attempt refresh for the refresh endpoint itself to avoid infinite loops
  const isRefreshRequest = config.url?.includes("/token/refresh");

  if (!isRefreshRequest) {
    const isTokenExpired = store.isTokenExpired();
    const isRefreshTokenExpired = store.isRefreshTokenExpired();

    if (isTokenExpired && !isRefreshTokenExpired) {
      console.debug("[Auth] Token expired, attempting refresh...");
      try {
        await refreshAuth();
        console.debug("[Auth] Token refresh successful");
      } catch (error) {
        console.warn("[Auth] Token refresh failed, logging out:", error);
        logoutAuth();
        document.location.reload();
      }
    }
  }

  const token = store.getToken();
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }

  try {
    const language = languageStore.getState().language ?? "en-US";
    config.headers.set("Accept-Language", language);
  } catch (e) {
    console.warn("[i18n] Failed to get language from store, defaulting to en-US", e);
    config.headers.set("Accept-Language", "en-US");
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      console.warn("[Auth] Received 403/401 forbidden response:", {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response.status,
        statusText: error.response.statusText,
      });
      logoutAuth();
      if (!document.location.pathname.includes("/login")) document.location.reload();
    }

    return Promise.reject(error instanceof Error ? error : new Error(error));
  }
);

const config = new Configuration({
  basePath: "/api",
  baseOptions: apiClient.defaults,
});

const Api = {
  account: new AccountApi(config, undefined, apiClient),
  carrier: new CarrierApi(config, undefined, apiClient),
  company: new CompanyApi(config, undefined, apiClient),
  delivery: new DeliveryApi(config, undefined, apiClient),
  reporting: new ReportingApi(config, undefined, apiClient),
  security: new SecurityApi(config, undefined, apiClient),
  settings: new SettingsApi(config, undefined, apiClient),
  subscription: new SubscriptionApi(config, undefined, apiClient),
  user: new UserApi(config, undefined, apiClient),
  onboarding: new OnboardApi(config, undefined, apiClient),
};

export { Api, apiClient };

/**
 * Initializes the API client by fetching the configuration and setting the base URL.
 * This function should be called during the app's startup process.
 */
export async function initializeApiClient() {
  try {
    const config = await fetchApiConfig();
    apiClient.defaults.baseURL = config.apiUrl;
    console.log("API client initialized with base URL:", config.apiUrl);
  } catch (error) {
    console.error("Failed to initialize API client:", error);
  }
}
